import { EVENT_TYPE_LABELS, type InquiryEventType } from "@/api/types/inquiry";
import type { NewLead } from "@/api/types/newLead";

/** "wedding" -> "Wedding"; unknown/custom values are just title-cased. */
export function eventTypeLabel(eventType: string | null): string | null {
  if (!eventType) return null;
  const known = EVENT_TYPE_LABELS[eventType as InquiryEventType];
  if (known) return known;
  return eventType.charAt(0).toUpperCase() + eventType.slice(1);
}

/** "2026-12-12" -> "Dec 12". Falls back to null on anything unparseable. */
export function shortEventDate(eventDate: string | null): string | null {
  if (!eventDate) return null;
  const date = new Date(eventDate);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(date);
}

/**
 * "Jane Smith, Wedding · Dec 12" — the headline fragment describing the
 * newest unactioned lead, used by both the banner and the arrival toast.
 * Degrades gracefully field-by-field so a sparsely-filled-out inquiry still
 * reads sensibly instead of showing "null" or empty segments.
 */
export function leadHeadline(lead: NewLead | undefined): string {
  if (!lead) return "New lead";

  const name = lead.contact_name?.trim() || "New lead";
  const parts: string[] = [];
  const type = eventTypeLabel(lead.event_type);
  if (type) parts.push(type);
  const date = shortEventDate(lead.event_date);
  if (date) parts.push(date);

  if (parts.length === 0) return name;
  return `${name}, ${parts.join(" · ")}`;
}
